
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

interface TagsInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
}

const TagsInput: React.FC<TagsInputProps> = ({ value = [], onChange }) => {
  const [inputValue, setInputValue] = useState('');
  
  // Ensure value is ALWAYS a valid array by creating a new array
  const tags = Array.isArray(value) ? [...value] : [];
  
  console.log('[TagsInput] Component rendered with value:', JSON.stringify(value), 'processed tags:', JSON.stringify(tags));

  // Clean tag function to ensure proper formatting
  const cleanTag = (tag: string) => {
    const cleaned = String(tag)
      .replace(/^["']|["']$/g, '') // Remove surrounding quotes
      .replace(/^#+/, '') // Remove any leading hashtags
      .replace(/[{}]/g, '') // Remove curly braces
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
    console.log('[TagsInput] Cleaned tag:', tag, '->', cleaned);
    return cleaned;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim() !== '') {
      e.preventDefault();
      const cleanedTag = cleanTag(inputValue);
      if (cleanedTag && !tags.some(existingTag => cleanTag(existingTag) === cleanedTag)) {
        // Create a new array with cleaned tag
        const newTags = [...tags, cleanedTag];
        console.log('[TagsInput] Adding cleaned tag:', cleanedTag, 'new tags:', JSON.stringify(newTags));
        onChange(newTags);
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      const newTags = tags.slice(0, -1);
      console.log('[TagsInput] Removing last tag, new tags:', JSON.stringify(newTags));
      onChange(newTags);
    }
  };

  const removeTag = (index: number) => {
    const newTags = [...tags];
    newTags.splice(index, 1);
    console.log('[TagsInput] Removing tag at index', index, 'new tags:', JSON.stringify(newTags), 'Type:', typeof newTags, 'Is Array:', Array.isArray(newTags));
    onChange(newTags);
  };

  // CRITICAL FIX: Add current input value as tag when input loses focus
  const handleBlur = () => {
    if (inputValue.trim() !== '') {
      const cleanedTag = cleanTag(inputValue);
      if (cleanedTag && !tags.some(existingTag => cleanTag(existingTag) === cleanedTag)) {
        const newTags = [...tags, cleanedTag];
        console.log('[TagsInput] Auto-adding tag on blur:', cleanedTag, 'new tags:', JSON.stringify(newTags));
        onChange(newTags);
        setInputValue('');
      }
    }
  };

  return (
    <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-background">
      {tags.map((tag, index) => {
        const displayTag = cleanTag(tag);
        return displayTag ? (
          <Badge key={index} variant="secondary" className="flex items-center gap-1">
            #{displayTag}
            <X
              className="h-3 w-3 cursor-pointer hover:text-destructive"
              onClick={() => removeTag(index)}
            />
          </Badge>
        ) : null;
      }).filter(Boolean)}
      <Input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className="flex-grow border-0 p-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
        placeholder={tags.length === 0 ? "Legg til emneord (trykk Enter eller klikk utenfor for å legge til)" : ""}
      />
    </div>
  );
};

export default TagsInput;
